import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import JobMonitor from '../components/JobMonitor';
import { Job, SlugPruefung } from '../types';

const SLUG_DEBOUNCE_MS = 400;

const ProvisionierenPage: React.FC = () => {
  const [firmenname, setFirmenname] = useState('');
  const [slug, setSlug] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [imageTag, setImageTag] = useState('latest');
  const [branche, setBranche] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [demoDaten, setDemoDaten] = useState(false);

  const [slugPruefung, setSlugPruefung] = useState<SlugPruefung | null>(null);
  const [slugPrueftGerade, setSlugPrueftGerade] = useState(false);
  const slugDebounceRef = useRef<number | null>(null);
  const slugAnfrageRef = useRef(0);

  const [job, setJob] = useState<Job | null>(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollJob = useCallback((id: number) => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const res = await api.get<Job>(`/api/jobs/${id}`);
        setJob(res.data);
        if (res.data.status !== 'LAEUFT') {
          stopPolling();
          setLaeuft(false);
        }
      } catch {
        stopPolling();
        setLaeuft(false);
      }
    }, 3000);
  }, []);

  useEffect(() => () => stopPolling(), []);

  // Live-Slug-Pruefung, debounced. Eine laufende Anfrage-Nummer verhindert,
  // dass eine spaete Antwort eine spaeter gestartete, aber schneller
  // zurueckgekommene Antwort ueberschreibt.
  useEffect(() => {
    if (slugDebounceRef.current) {
      window.clearTimeout(slugDebounceRef.current);
      slugDebounceRef.current = null;
    }
    const eingabe = slug.trim();
    if (!eingabe) {
      setSlugPruefung(null);
      setSlugPrueftGerade(false);
      return;
    }
    setSlugPrueftGerade(true);
    const anfrageId = ++slugAnfrageRef.current;
    slugDebounceRef.current = window.setTimeout(async () => {
      try {
        const res = await api.get<SlugPruefung>('/api/provisionierung/pruefe-slug', {
          params: { slug: eingabe },
        });
        if (anfrageId === slugAnfrageRef.current) {
          setSlugPruefung(res.data);
        }
      } catch {
        if (anfrageId === slugAnfrageRef.current) {
          setSlugPruefung(null);
        }
      } finally {
        if (anfrageId === slugAnfrageRef.current) {
          setSlugPrueftGerade(false);
        }
      }
    }, SLUG_DEBOUNCE_MS);
    return () => {
      if (slugDebounceRef.current) {
        window.clearTimeout(slugDebounceRef.current);
      }
    };
  }, [slug]);

  const slugFrei = !!slugPruefung?.frei;
  const kannAbsenden =
    !laeuft &&
    !slugPrueftGerade &&
    slugFrei &&
    firmenname.trim() !== '' &&
    slug.trim() !== '' &&
    adminEmail.trim() !== '';

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kannAbsenden) return;
    setFehler('');
    setLaeuft(true);
    try {
      const res = await api.post<Job>('/api/instanzen/provisionieren', {
        slug: slug.trim(),
        firmenname: firmenname.trim(),
        admin_email: adminEmail.trim(),
        image_tag: imageTag.trim() || 'latest',
        anthropic_key: anthropicKey.trim() || null,
        branche: branche.trim() || null,
        demo_daten: demoDaten,
      });
      setJob(res.data);
      pollJob(res.data.id);
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Provisionierung konnte nicht gestartet werden.');
      setLaeuft(false);
    }
  };

  const fortsetzen = async () => {
    if (!job) return;
    setFehler('');
    setLaeuft(true);
    try {
      const res = await api.post<Job>(`/api/jobs/${job.id}/fortsetzen`);
      setJob(res.data);
      pollJob(res.data.id);
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Fortsetzen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="k-seite">
      <div className="k-seite__kopf">
        <div>
          <span className="k-eyebrow">{'// fleet / provisionieren'}</span>
          <h1 className="k-h1">Provisionieren</h1>
        </div>
      </div>

      {fehler && <div className="k-fehler">{fehler}</div>}

      <form className="k-formular" onSubmit={absenden}>
        <div className="k-feld">
          <label>Firmenname</label>
          <input
            value={firmenname}
            onChange={e => setFirmenname(e.target.value)}
            disabled={laeuft}
            required
            autoFocus
          />
        </div>

        <div className="k-feld">
          <label>Slug</label>
          <input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            disabled={laeuft}
            required
          />
          {slugPrueftGerade && (
            <span className="k-feld__hint">Prüfe Verfügbarkeit …</span>
          )}
          {!slugPrueftGerade && slugPruefung && slugPruefung.frei && (
            <span className="k-feld__hint">
              Frei — die Instanz wird unter{' '}
              <span className="k-mono">{slugPruefung.host}</span> erreichbar sein.
            </span>
          )}
          {!slugPrueftGerade && slugPruefung && !slugPruefung.frei && (
            <span className="k-feld__hint">{slugPruefung.grund}</span>
          )}
        </div>

        <div className="k-feld">
          <label>Admin-E-Mail</label>
          <input
            type="email"
            value={adminEmail}
            onChange={e => setAdminEmail(e.target.value)}
            disabled={laeuft}
            required
          />
        </div>

        <div className="k-feld">
          <label>Image-Tag</label>
          <input
            value={imageTag}
            onChange={e => setImageTag(e.target.value)}
            disabled={laeuft}
          />
        </div>

        <div className="k-feld">
          <label>Branche (optional)</label>
          <input value={branche} onChange={e => setBranche(e.target.value)} disabled={laeuft} />
        </div>

        <div className="k-feld">
          <label>ANTHROPIC_API_KEY (optional)</label>
          <input
            type="password"
            value={anthropicKey}
            onChange={e => setAnthropicKey(e.target.value)}
            disabled={laeuft}
            placeholder="leer = keine KI-Funktionen in der Instanz"
          />
        </div>

        <div className="k-feld">
          <label>
            <input
              type="checkbox"
              checked={demoDaten}
              onChange={e => setDemoDaten(e.target.checked)}
              disabled={laeuft}
            />{' '}
            Demo-Daten einspielen
          </label>
        </div>

        <div className="k-hinweis">
          DNS wird manuell gesetzt: Lege vor dem Start bei Strato einen A-Record{' '}
          <span className="k-mono">{slugPruefung?.host || `${slug || '<slug>'}.<basis-domain>`}</span>{' '}
          auf die Server-IP an. Fehlt er, stoppt der Job bei der DNS-Prüfung — du kannst
          ihn danach fortsetzen.
        </div>

        <div className="k-hinweis">
          Der Schritt „Zugangsdaten" zeigt das generierte Admin-Passwort im Klartext im
          Log an — es erscheint nur einmal dort und sollte sofort notiert werden.
        </div>

        <button className="k-btn" type="submit" disabled={!kannAbsenden}>
          {laeuft ? 'Wird provisioniert …' : 'Instanz provisionieren'}
        </button>
      </form>

      {job && <JobMonitor job={job} onFortsetzen={fortsetzen} laeuft={laeuft} />}
    </div>
  );
};

export default ProvisionierenPage;
